import axios, { AxiosError, AxiosPromise } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { InternalAxiosRequestConfig } from 'axios';
import { OrderStatusServiceApiFactory } from '@openapi/orderstatus/order_status/api';

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const axiosInstance = axios.create({
  headers,
  timeout: 60000,
});

axiosInstance.interceptors.response.use(
  (response) => {
    const data = response.data as Record<string, unknown>;
    return {
      ...response,
      data: camelcaseKeys(data, {
        deep: true,
        exclude: [/-/],
      }),
    };
  },
  (error: AxiosError) => {
    const data = error.response?.data as Record<string, unknown>;
    const convertedError = { ...error };
    if (data && convertedError.response) {
      convertedError.response.data = camelcaseKeys(data, { deep: true });
    }
    return Promise.reject(convertedError);
  },
);

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig<any>) => {
    const { data } = config;

    let parsedData;
    try {
      parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
      parsedData = data;
    }

    if (parsedData) {
      return {
        ...config,
        data: snakecaseKeys(parsedData, {
          deep: true,
          exclude: [/-/],
        }),
      };
    }

    return config;
  },
);

type ApiFactory = {
  [key: string]: (...args: any) => AxiosPromise<any>;
};
type Factory<T> = (...args: any[]) => T;

const initFactry = <T extends ApiFactory>(factory: Factory<T>): T =>
  factory(
    undefined,
    process.env.REACT_APP_KIOSK_API_URL || 'http://localhost:6006', // NOTE: Storybookでは環境変数が使えないので、読み込めない場合は「localhost:6006」を使う
    axiosInstance,
  );

export const orderStatusServiceFactory = initFactry(
  OrderStatusServiceApiFactory,
);
